import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import Modal from "@/components/ui/modal";
import Dropdown from "@/components/ui/dropdown";
import { getAdminUsers } from "@/lib/users.api";
import { rolesAndPermissionsApi } from "@/features/roles-and-permissions/services/roles-and-permissions.api";
import { departmentsApi } from "@/features/departments/services/departments.api";
import type { SendSmsPayload, SmsRecipientTarget } from "../types/sms-broadcast.types";
import styles from "./SendSmsModal.module.scss";

const PAGE_SIZE = 10;

type Option = { label: string; value: string };

function mergeOptions(prev: Option[], next: Option[]): Option[] {
  const seen = new Set(prev.map((o) => o.value));
  const merged = [...prev];
  for (const o of next) {
    if (!seen.has(o.value)) {
      seen.add(o.value);
      merged.push(o);
    }
  }
  return merged;
}

function hasNextPage(
  batchLength: number,
  mergedLength: number,
  total: number,
): boolean {
  if (batchLength < PAGE_SIZE) return false;
  if (
    Number.isFinite(total) &&
    total > 0 &&
    mergedLength >= total
  )
    return false;
  return true;
}

interface SendSmsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: SendSmsPayload) => void | Promise<void>;
}

const TARGETS: SmsRecipientTarget[] = ["all", "staff", "roles", "departments"];

const SendSmsModal: React.FC<SendSmsModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [target, setTarget] = useState<SmsRecipientTarget>("all");
  const [message, setMessage] = useState("");
  const [staffIds, setStaffIds] = useState<string[]>([]);
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [departmentIds, setDepartmentIds] = useState<string[]>([]);
  const [staffSearch, setStaffSearch] = useState("");
  const [debouncedStaffSearch, setDebouncedStaffSearch] = useState("");
  const [staffOptions, setStaffOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [roleOptions, setRoleOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [departmentOptions, setDepartmentOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [rolesHasMore, setRolesHasMore] = useState(true);
  const [deptsHasMore, setDeptsHasMore] = useState(true);
  const [staffHasMore, setStaffHasMore] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const roleNextPageRef = useRef(1);
  const deptNextPageRef = useRef(1);
  const staffNextPageRef = useRef(1);
  const debouncedStaffSearchRef = useRef(debouncedStaffSearch);

  useEffect(() => {
    debouncedStaffSearchRef.current = debouncedStaffSearch;
  }, [debouncedStaffSearch]);

  useEffect(() => {
    const tmr = setTimeout(() => setDebouncedStaffSearch(staffSearch), 300);
    return () => clearTimeout(tmr);
  }, [staffSearch]);

  useEffect(() => {
    if (!isOpen) {
      setRoleOptions([]);
      setDepartmentOptions([]);
      setStaffOptions([]);
      setLoadingRoles(false);
      setLoadingDepartments(false);
      setLoadingStaff(false);
      setRolesHasMore(true);
      setDeptsHasMore(true);
      setStaffHasMore(true);
      roleNextPageRef.current = 1;
      deptNextPageRef.current = 1;
      staffNextPageRef.current = 1;
      return;
    }
    setTarget("all");
    setMessage("");
    setStaffIds([]);
    setRoleIds([]);
    setDepartmentIds([]);
    setStaffSearch("");
    setDebouncedStaffSearch("");
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoadingRoles(true);
    roleNextPageRef.current = 1;
    setRolesHasMore(true);
    rolesAndPermissionsApi
      .getAll({ page: 1, pageSize: PAGE_SIZE })
      .then((res) => {
        if (cancelled) return;
        const opts = res.data.map((r) => ({
          value: r.id,
          label: r.roleName || r.id,
        }));
        setRoleOptions(opts);
        roleNextPageRef.current = 2;
        setRolesHasMore(
          hasNextPage(res.data.length, opts.length, res.total),
        );
      })
      .catch(() => {
        if (!cancelled) {
          setRoleOptions([]);
          setRolesHasMore(false);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingRoles(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const loadMoreRoles = useCallback(async (): Promise<boolean> => {
    const page = roleNextPageRef.current;
    try {
      const res = await rolesAndPermissionsApi.getAll({
        page,
        pageSize: PAGE_SIZE,
      });
      const newOpts = res.data.map((r) => ({
        value: r.id,
        label: r.roleName || r.id,
      }));
      let mergedLen = 0;
      setRoleOptions((prev) => {
        const merged = mergeOptions(prev, newOpts);
        mergedLen = merged.length;
        return merged;
      });
      roleNextPageRef.current = page + 1;
      const more = hasNextPage(res.data.length, mergedLen, res.total);
      setRolesHasMore(more);
      return more;
    } catch {
      setRolesHasMore(false);
      return false;
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoadingDepartments(true);
    deptNextPageRef.current = 1;
    setDeptsHasMore(true);
    departmentsApi
      .getList({ page: 1, limit: PAGE_SIZE })
      .then((res) => {
        if (cancelled) return;
        const opts = res.data
          .map((d) => {
            const raw = d as typeof d & { ID?: string };
            const id = String(raw.id ?? raw.ID ?? "").trim();
            const name = d.name ?? "";
            return { value: id, label: name };
          })
          .filter((o) => o.value !== "");
        setDepartmentOptions(opts);
        deptNextPageRef.current = 2;
        setDeptsHasMore(hasNextPage(res.data.length, opts.length, res.total));
      })
      .catch(() => {
        if (!cancelled) {
          setDepartmentOptions([]);
          setDeptsHasMore(false);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingDepartments(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const loadMoreDepartments = useCallback(async (): Promise<boolean> => {
    const page = deptNextPageRef.current;
    try {
      const res = await departmentsApi.getList({ page, limit: PAGE_SIZE });
      const newOpts = res.data
        .map((d) => {
          const raw = d as typeof d & { ID?: string };
          const id = String(raw.id ?? raw.ID ?? "").trim();
          const name = d.name ?? "";
          return { value: id, label: name };
        })
        .filter((o) => o.value !== "");
      let mergedLen = 0;
      setDepartmentOptions((prev) => {
        const merged = mergeOptions(prev, newOpts);
        mergedLen = merged.length;
        return merged;
      });
      deptNextPageRef.current = page + 1;
      const more = hasNextPage(res.data.length, mergedLen, res.total);
      setDeptsHasMore(more);
      return more;
    } catch {
      setDeptsHasMore(false);
      return false;
    }
  }, []);

  useEffect(() => {
    if (!isOpen || target !== "staff") return;
    let cancelled = false;
    setLoadingStaff(true);
    staffNextPageRef.current = 1;
    setStaffHasMore(true);
    getAdminUsers({
      page: 1,
      limit: PAGE_SIZE,
      search: debouncedStaffSearch.trim() || undefined,
    })
      .then(({ data, total }) => {
        if (cancelled) return;
        const opts = data.map((u) => ({
          value: u.id,
          label:
            [u.firstName, u.lastName].filter(Boolean).join(" ") ||
            u.email ||
            u.id,
        }));
        setStaffOptions(opts);
        staffNextPageRef.current = 2;
        setStaffHasMore(hasNextPage(data.length, opts.length, total));
      })
      .catch(() => {
        if (!cancelled) {
          setStaffOptions([]);
          setStaffHasMore(false);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingStaff(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, target, debouncedStaffSearch]);

  const loadMoreStaff = useCallback(async (): Promise<boolean> => {
    const page = staffNextPageRef.current;
    const search =
      debouncedStaffSearchRef.current.trim() || undefined;
    try {
      const { data, total } = await getAdminUsers({
        page,
        limit: PAGE_SIZE,
        search,
      });
      const newOpts = data.map((u) => ({
        value: u.id,
        label:
          [u.firstName, u.lastName].filter(Boolean).join(" ") ||
          u.email ||
          u.id,
      }));
      let mergedLen = 0;
      setStaffOptions((prev) => {
        const merged = mergeOptions(prev, newOpts);
        mergedLen = merged.length;
        return merged;
      });
      staffNextPageRef.current = page + 1;
      const more = hasNextPage(data.length, mergedLen, total);
      setStaffHasMore(more);
      return more;
    } catch {
      setStaffHasMore(false);
      return false;
    }
  }, []);

  const targetLabel = useCallback(
    (v: SmsRecipientTarget) => {
      const map: Record<SmsRecipientTarget, string> = {
        all: t("smsBroadcast.sendModal.targetAll"),
        staff: t("smsBroadcast.sendModal.targetStaff"),
        roles: t("smsBroadcast.sendModal.targetRoles"),
        departments: t("smsBroadcast.sendModal.targetDepartments"),
      };
      return map[v];
    },
    [t],
  );

  const isFormValid = useMemo(() => {
    const msgOk = message.trim().length > 0;
    if (!msgOk) return false;
    if (target === "all") return true;
    if (target === "staff") return staffIds.length > 0;
    if (target === "roles") return roleIds.length > 0;
    if (target === "departments") return departmentIds.length > 0;
    return false;
  }, [message, target, staffIds.length, roleIds.length, departmentIds.length]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isFormValid || submitting) return;
    const payload: SendSmsPayload = {
      message: message.trim(),
      target,
      staffIds: target === "staff" ? staffIds : undefined,
      roleIds: target === "roles" ? roleIds : undefined,
      departmentIds: target === "departments" ? departmentIds : undefined,
    };
    setSubmitting(true);
    try {
      await onSubmit(payload);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const staffSelectedLabel =
    staffIds.length === 0
      ? undefined
      : staffIds
          .map((id) => staffOptions.find((o) => o.value === id)?.label ?? id)
          .join(", ");

  const roleSelectedLabel =
    roleIds.length === 0
      ? undefined
      : roleIds
          .map((id) => roleOptions.find((o) => o.value === id)?.label ?? id)
          .join(", ");

  const deptSelectedLabel =
    departmentIds.length === 0
      ? undefined
      : departmentIds
          .map(
            (id) =>
              departmentOptions.find((o) => o.value === id)?.label ?? id,
          )
          .join(", ");

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("smsBroadcast.sendModal.title")}
      size="lg"
      withForm
      formId="sendSmsForm"
      handleSubmit={handleSubmit}
      loading={submitting}
      isFormValid={isFormValid}
      submitButtonText={t("smsBroadcast.sendModal.submit")}
    >
      <div className={styles.body}>
        <p className={styles.hint}>
          {t("smsBroadcast.sendModal.backendHint")}
        </p>

        <div className={styles.field}>
          <span className={styles.label}>
            {t("smsBroadcast.sendModal.targetLabel")}
          </span>
          <div className={styles.targetRow}>
            {TARGETS.map((v) => (
              <button
                key={v}
                type="button"
                className={`${styles.targetBtn} ${
                  target === v ? styles.targetBtnActive : ""
                }`}
                onClick={() => setTarget(v)}
              >
                {targetLabel(v)}
              </button>
            ))}
          </div>
        </div>

        {target === "staff" && (
          <Dropdown
            label={t("smsBroadcast.sendModal.staff")}
            multiple
            searchable
            writable
            disabled={loadingStaff}
            hasMore={staffHasMore}
            onScrollEnd={() => loadMoreStaff()}
            options={staffOptions}
            value={staffIds}
            selectedLabel={staffSelectedLabel}
            onSelect={(v) =>
              setStaffIds(Array.isArray(v) ? v : v ? [v] : [])
            }
            onSearch={setStaffSearch}
            placeholder={
              loadingStaff
                ? t("loading")
                : t("smsBroadcast.sendModal.staffPlaceholder")
            }
          />
        )}

        {target === "roles" && (
          <Dropdown
            label={t("smsBroadcast.sendModal.roles")}
            multiple
            searchable
            disabled={loadingRoles}
            hasMore={rolesHasMore}
            onScrollEnd={() => loadMoreRoles()}
            options={roleOptions}
            value={roleIds}
            selectedLabel={roleSelectedLabel}
            onSelect={(v) =>
              setRoleIds(Array.isArray(v) ? v : v ? [v] : [])
            }
            placeholder={
              loadingRoles
                ? t("loading")
                : t("smsBroadcast.sendModal.rolesPlaceholder")
            }
          />
        )}

        {target === "departments" && (
          <Dropdown
            label={t("smsBroadcast.sendModal.departments")}
            multiple
            searchable
            disabled={loadingDepartments}
            hasMore={deptsHasMore}
            onScrollEnd={() => loadMoreDepartments()}
            options={departmentOptions}
            value={departmentIds}
            selectedLabel={deptSelectedLabel}
            onSelect={(v) =>
              setDepartmentIds(Array.isArray(v) ? v : v ? [v] : [])
            }
            placeholder={
              loadingDepartments
                ? t("loading")
                : t("smsBroadcast.sendModal.departmentsPlaceholder")
            }
          />
        )}

        <div className={styles.field}>
          <label className={styles.label} htmlFor="sms-message">
            {t("smsBroadcast.sendModal.message")}
          </label>
          <textarea
            id="sms-message"
            className={styles.textarea}
            rows={5}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={918}
            placeholder={t("smsBroadcast.sendModal.messagePlaceholder")}
          />
        </div>
      </div>
    </Modal>
  );
};

export default SendSmsModal;
